import { useQuery } from '@tanstack/react-query';
import { queryLogs, queryRequests, resolveUserTags, type LogQueryParams } from '../api/observability';
import { queryKeys } from '@/lib/pwa/queryConfig';
import { queryPersistMeta } from '@/lib/pwa/queryPersistence';

// Tela de observabilidade é para depuração de bugs. Cache (persistido OU in-memory)
// atrapalha: hidrata dados velhos no mount, mascara erros de refetch, e dá impressão
// de "tela travada". staleTime=0 + gcTime=0 força fetch a cada mount/refetch e descarta
// o resultado quando a query desmonta. queryPersistMeta.never bloqueia também o
// snapshot do IndexedDB do PWA.
const NO_CACHE_OPTS = {
  staleTime: 0,
  gcTime: 0,
  meta: queryPersistMeta.never,
} as const;

export function useLogQuery(
  params: LogQueryParams,
  enabled = true,
  refetchInterval: number | false = false,
) {
  return useQuery({
    queryKey: [...queryKeys.admin.logs, params],
    queryFn: () => queryLogs(params),
    enabled,
    refetchInterval,
    ...NO_CACHE_OPTS,
  });
}

export function useRequestQuery(
  params: LogQueryParams,
  enabled = true,
  refetchInterval: number | false = false,
) {
  return useQuery({
    queryKey: [...queryKeys.admin.requests, params],
    queryFn: () => queryRequests(params),
    enabled,
    refetchInterval,
    ...NO_CACHE_OPTS,
  });
}

/**
 * Resolve user tags (emails) para nomes completos.
 * Cacheia agressivamente — nomes de usuário mudam raramente.
 */
export function useUserNames(tags: string[]) {
  const uniqueTags = [...new Set(tags.filter((t) => t && t !== '-'))];

  return useQuery({
    queryKey: [...queryKeys.admin.userNames, uniqueTags.sort().join(',')],
    queryFn: () => resolveUserTags(uniqueTags),
    enabled: uniqueTags.length > 0,
    staleTime: 5 * 60_000, // 5 min — nomes mudam raramente
    gcTime: 30 * 60_000,
  });
}
